import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  Box
} from '@mui/material';
import {
  Visibility as ViewIcon,
  FileCopy as UseTemplateIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  fetchPublicTemplates
} from './visualStrategiesSlice';
import { CardSkeleton } from '../../components/LoadingSpinner';
import DWLFLoader from '../../components/DWLFLoader';

const TemplatesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    templates,
    templatesLoading,
    templatesError
  } = useSelector((state) => state.visualStrategies);

  useEffect(() => {
    dispatch(fetchPublicTemplates());
  }, [dispatch]);

  const handleUseTemplate = (template) => {
    navigate('/strategies/new', {
      state: {
        duplicateFrom: template,
        name: `${template.name} (My Copy)`
      }
    });
  };

  const handleViewTemplate = (template) => {
    // Could open a read-only preview modal or navigate to view mode
    console.log('View template:', template);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'simple': return 'success';
      case 'moderate': return 'warning';
      case 'complex': return 'error';
      default: return 'default';
    }
  };

  if (templatesLoading) {
    return (
      <Container maxWidth="xl" style={{ padding: '20px' }}>
        <Typography variant="h4" gutterBottom>
          Strategy Templates
        </Typography>
        <DWLFLoader size="large" />
      </Container>
    );
  }

  if (templatesError) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {templatesError}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" style={{ padding: '20px' }}>
      <Typography variant="h4" gutterBottom>
        Strategy Templates
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Explore pre-built strategy templates created by the community. Use them as starting points for your own strategies.
      </Typography>

      <Grid container spacing={3}>
        {templates.map((template, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.strategyId}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {template.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {template.description || 'No description available'}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip
                      label={template.stats?.complexity || 'Unknown'}
                      color={getComplexityColor(template.stats?.complexity)}
                      size="small"
                    />
                    {template.metadata?.timeframe && (
                      <Chip
                        label={template.metadata.timeframe}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    {template.stats?.totalNodes || 0} nodes • {template.stats?.totalConnections || 0} connections
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Created: {formatDate(template.createdAt)}
                  </Typography>
                </CardContent>
                
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewTemplate(template)}
                  >
                    Preview
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<UseTemplateIcon />}
                    onClick={() => handleUseTemplate(template)}
                    sx={{ 
                      backgroundColor: 'var(--dwlf-accent)',
                      color: 'var(--dwlf-bg)',
                      '&:hover': {
                        backgroundColor: 'var(--dwlf-text)',
                      }
                    }}
                  >
                    Use Template
                  </Button>
                </CardActions>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {templates.length === 0 && !templatesLoading && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No templates available yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for community-created strategy templates.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default TemplatesPage; 